import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Trash2, Plus, Target, Search, Archive, Copy, CalendarClock, XCircle, Eye } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Input } from '../../../components/ui/Input';
import { StudentData } from './StudentDetailPanel';
import { GroupId } from '../../../config/systemConfig';
import { error as logError } from '../../../utils/logger';
import type { PracticeAssignment } from '../../../assessment';
import { CreatePracticePanel } from '../components/CreatePracticePanel';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog';
import { EmptyState } from '../../../components/shared/EmptyState';
import { DataFreshness } from '../../shared/DataFreshness';
import { ClassroomSwitcher } from '../components/ClassroomSwitcher';
import { dataLayer } from '../../../data/dataLayer';
import type { Account, Classroom } from '../../../accounts';
import { containerVariants } from '../shared/animationVariants';
import { genId } from '../../../utils/genId';
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 24
    }
  }
};
interface HomeworkPageProps {
  students: StudentData[];
  selectedGroup: GroupId;
  activeAccount?: Account;
  classrooms?: Classroom[];
  selectedClassroomId?: string | null;
  onSelectClassroom?: (id: string | null) => void;
  classroomName?: string;
}
type HomeworkTab = 'open' | 'klaar' | 'archief';
type DerivedStatus = 'open' | 'klaar' | 'verlopen' | 'archief';
interface DerivedRow {
  assignment: PracticeAssignment;
  targetCount: number;
  completedCount: number;
  progressPercent: number;
  derived: DerivedStatus;
}
function deriveStatus(a: PracticeAssignment, targetCount: number, completedCount: number): DerivedStatus {
  if (a.status === 'archived') return 'archief';
  if (completedCount >= targetCount && targetCount > 0) return 'klaar';
  if (a.deadline && Date.now() > a.deadline) return 'verlopen';
  if (a.status === 'completed') return 'klaar';
  return 'open';
}
export function HomeworkPage({
  students,
  selectedGroup,
  activeAccount,
  classrooms = [],
  selectedClassroomId,
  onSelectClassroom,
  classroomName
}: HomeworkPageProps) {
  const [assignments, setAssignments] = useState<PracticeAssignment[]>([]);
  const [tab, setTab] = useState<HomeworkTab>('open');
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [lastLoadedAt, setLastLoadedAt] = useState(() => new Date());
  const [confirmAction, setConfirmAction] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    action: () => Promise<void>;
  }>({
    isOpen: false,
    title: '',
    message: '',
    action: async () => {}
  });
  const loadData = useCallback(async () => {
    try {
      const data = await dataLayer.getPracticeAssignments();
      const all = Object.values(data);
      const relevant = all.filter(a => (a.classroomId || null) === (selectedClassroomId || null));
      setAssignments(relevant.sort((a, b) => b.createdAt - a.createdAt));
      setLastLoadedAt(new Date());
    } catch (err) {
      logError('[HomeworkPage] loadData failed:', err);
      setAssignments([]);
    }
  }, [selectedClassroomId]);
  useEffect(() => {
    loadData();
  }, [loadData]);
  const derivedRows: DerivedRow[] = useMemo(() => {
    return assignments.map(a => {
      const targetCount = a.wholeClass ? students.length : a.targetStudentIds.length;
      const completedCount = a.completedByStudentIds.length;
      const progressPercent = targetCount > 0 ? Math.round(completedCount / targetCount * 100) : 0;
      return {
        assignment: a,
        targetCount,
        completedCount,
        progressPercent,
        derived: deriveStatus(a, targetCount, completedCount)
      };
    });
  }, [assignments, students.length]);
  const counts = useMemo(() => {
    const c = {
      open: 0,
      klaar: 0,
      archief: 0
    };
    derivedRows.forEach(r => {
      if (r.derived === 'archief') c.archief++;else if (r.derived === 'klaar') c.klaar++;else c.open++; // open + verlopen show under "Open" so teacher can act
    });
    return c;
  }, [derivedRows]);
  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return derivedRows.filter(r => {
      // tab filter
      if (tab === 'open') {
        if (r.derived !== 'open' && r.derived !== 'verlopen') return false;
      } else if (tab === 'klaar') {
        if (r.derived !== 'klaar') return false;
      } else {
        if (r.derived !== 'archief') return false;
      }
      // search filter
      if (q && !r.assignment.title.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [derivedRows, tab, searchQuery]);
  const handleDelete = async (id: string) => {
    setConfirmAction({
      isOpen: true,
      title: 'Huiswerk verwijderen',
      message: 'Weet je zeker dat je dit huiswerk wilt verwijderen?',
      action: async () => {
        await dataLayer.deletePracticeAssignment(id);
        loadData();
      }
    });
  };
  const handleArchive = async (id: string) => {
    setConfirmAction({
      isOpen: true,
      title: 'Huiswerk archiveren',
      message: 'Dit huiswerk verdwijnt uit "Open" en gaat naar het archief.',
      action: async () => {
        await dataLayer.archivePracticeAssignment(id);
        loadData();
      }
    });
  };
  const handleWithdraw = async (id: string) => {
    setConfirmAction({
      isOpen: true,
      title: 'Huiswerk intrekken',
      message: 'Wil je dit huiswerk intrekken? Leerlingen zien het dan niet meer. Voortgang blijft bewaard.',
      action: async () => {
        await dataLayer.archivePracticeAssignment(id);
        loadData();
      }
    });
  };
  const handleExtendDeadline = async (assignment: PracticeAssignment) => {
    const current = assignment.deadline ?? Date.now();
    const newDeadline = current + 7 * 24 * 60 * 60 * 1000;
    try {
      await dataLayer.savePracticeAssignment(assignment.id, {
        ...assignment,
        deadline: newDeadline
      });
      loadData();
    } catch (err) {
      logError('[HomeworkPage] extend deadline failed:', err);
    }
  };
  const handleDuplicate = async (assignment: PracticeAssignment) => {
    if (!activeAccount) return;
    try {
      const newId = genId('pa');
      const copy: PracticeAssignment = {
        ...assignment,
        id: newId,
        teacherId: activeAccount.id,
        title: `${assignment.title} (kopie)`,
        createdAt: Date.now(),
        status: 'active',
        completedByStudentIds: []
      };
      await dataLayer.savePracticeAssignment(newId, copy);
      loadData();
    } catch (err) {
      logError('[HomeworkPage] duplicate failed:', err);
    }
  };
  const handleCreateClose = () => {
    setIsCreateOpen(false);
    loadData();
  };
  const TabButton = ({
    id,
    label,
    count
  }: {
    id: HomeworkTab;
    label: string;
    count: number;
  }) => <button onClick={() => setTab(id)} className={`relative px-5 py-2.5 rounded-lg text-sm font-bold transition-colors z-10 flex items-center gap-2 ${tab === id ? 'text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-8899">
      {tab === id && <motion.div layoutId="hwTabIndicator" className="absolute inset-0 bg-white rounded-lg shadow-sm" initial={false} transition={{
      type: 'spring',
      stiffness: 500,
      damping: 30
    }} data-id="element-8900" />}
      <span className="relative z-10" data-id="element-8901">{label}</span>
      <span className={`relative z-10 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold ${tab === id ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-200 text-slate-600'}`} data-id="element-8902">
        {count}
      </span>
    </button>;
  return <PageLayout title="Huiswerk" description={classroomName ? `Open, klaar en archief — ${classroomName}` : 'Open, klaar en archief'} data-id="element-8903">
      <motion.div className="max-w-5xl mx-auto space-y-6 pb-12" variants={containerVariants} initial="hidden" animate="visible" data-id="element-8904">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-[-12px]" data-id="element-8905">
          {classrooms.length > 0 && onSelectClassroom ? <ClassroomSwitcher classrooms={classrooms} selectedClassroomId={selectedClassroomId || null} onSelectClassroom={onSelectClassroom} activeTeacherId={activeAccount?.id} data-id="element-8906" /> : <span data-id="element-8907" />}
          <DataFreshness updatedAt={lastLoadedAt} onRefresh={loadData} data-id="element-8908" />
        </div>

        {/* Primary CTA */}
        <motion.div variants={itemVariants} data-id="element-8909">
          <Card className="p-5 border-l-4 border-l-indigo-400 border-slate-200 shadow-sm bg-white flex flex-col sm:flex-row items-start sm:items-center gap-4" data-id="element-8910">
            <div className="flex-1" data-id="element-8911">
              <h2 className="text-lg font-black text-slate-900 mb-1" data-id="element-8912">
                Huiswerk maken
              </h2>
              <p className="text-sm text-slate-600 leading-relaxed" data-id="element-8913">
                Geef leerlingen gericht oefenwerk op voor specifieke vakken of
                onderdelen.
              </p>
            </div>
            <Button onClick={() => setIsCreateOpen(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm" size="sm" data-id="element-8914">
              <Plus className="w-4 h-4 mr-2" data-id="element-8915" />
              Huiswerk maken
            </Button>
          </Card>
        </motion.div>

        {/* Tabs */}
        <motion.div variants={itemVariants} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-id="element-8916">
          <div className="bg-slate-100 p-1 rounded-xl inline-flex relative" data-id="element-8917">
            <TabButton id="open" label="Open" count={counts.open} data-id="element-8918" />
            <TabButton id="klaar" label="Klaar" count={counts.klaar} data-id="element-8919" />
            <TabButton id="archief" label="Archief" count={counts.archief} data-id="element-8920" />
          </div>
          <div className="w-full sm:w-64" data-id="element-8921">
            <Input placeholder="Zoek huiswerk..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-8923" />} size="sm" data-id="element-8922" />
          </div>
        </motion.div>

        {/* Rows */}
        {filteredRows.length > 0 ? <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-4" variants={containerVariants} initial="hidden" animate="visible" data-id="element-8924">
            {filteredRows.map(({
          assignment,
          targetCount,
          completedCount,
          progressPercent,
          derived
        }) => {
          const notStarted = targetCount - completedCount;
          return <motion.div key={assignment.id} variants={itemVariants} data-id="element-8925">
                    <Card className="p-4 border-slate-200 flex flex-col bg-white shadow-sm hover:shadow-md transition-shadow h-full" data-id="element-8926">
                      <div className="flex items-start justify-between mb-3 gap-2" data-id="element-8927">
                        <div className="flex items-start gap-3 min-w-0 flex-1" data-id="element-8928">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-indigo-100 text-indigo-600 flex-shrink-0" data-id="element-8929">
                            <Target className="w-4 h-4" data-id="element-8930" />
                          </div>
                          <div className="min-w-0 flex-1" data-id="element-8931">
                            <h3 className="font-bold text-sm text-slate-900 truncate" data-id="element-8932">
                              {assignment.title}
                            </h3>
                            <div className="text-[10px] text-slate-500 mt-0.5 truncate" data-id="element-8933">
                              {assignment.moduleIds.length} vak
                              {assignment.moduleIds.length === 1 ? '' : 'ken'} · {assignment.targetFamilyIds.length}{' '}
                              onderdel
                              {assignment.targetFamilyIds.length === 1 ? '' : 'en'}
                            </div>
                            <div className="text-[10px] text-slate-500 flex items-center gap-1.5 mt-1 flex-wrap" data-id="element-8934">
                              <Calendar className="w-3 h-3 flex-shrink-0" data-id="element-8935" />
                              {new Date(assignment.createdAt).toLocaleDateString('nl-NL')}
                              {assignment.deadline && <>
                                  <span data-id="element-8936">•</span>
                                  <span className={derived === 'verlopen' ? 'text-rose-600 font-bold' : 'text-orange-600'} data-id="element-8937">
                                    Deadline:{' '}
                                    {new Date(assignment.deadline).toLocaleDateString('nl-NL')}
                                  </span>
                                </>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-0.5 flex-shrink-0" data-id="element-8938">
                          {derived === 'open' || derived === 'verlopen' ? <>
                              <button onClick={() => handleExtendDeadline(assignment)} className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors" title="Deadline +7 dagen" aria-label="Deadline verlengen" data-id="element-8939">
                                <CalendarClock className="w-4 h-4" data-id="element-8940" />
                              </button>
                              <button onClick={() => handleWithdraw(assignment.id)} className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-md transition-colors" title="Intrekken" aria-label="Huiswerk intrekken" data-id="element-8941">
                                <XCircle className="w-4 h-4" data-id="element-8942" />
                              </button>
                            </> : <button className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors" title="Bekijken" aria-label="Huiswerk bekijken" data-id="element-8943">
                              <Eye className="w-4 h-4" data-id="element-8944" />
                            </button>}
                          <button onClick={() => handleDuplicate(assignment)} className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors" title="Dupliceren" aria-label="Huiswerk dupliceren" data-id="element-8945">
                            <Copy className="w-4 h-4" data-id="element-8946" />
                          </button>
                          {derived !== 'archief' && <button onClick={() => handleArchive(assignment.id)} className="p-1.5 text-slate-400 hover:text-amber-500 hover:bg-amber-50 rounded-md transition-colors" title="Archiveren" aria-label="Archiveren" data-id="element-8947">
                              <Archive className="w-4 h-4" data-id="element-8948" />
                            </button>}
                          <button onClick={() => handleDelete(assignment.id)} className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-md transition-colors" title="Verwijderen" aria-label="Verwijderen" data-id="element-8949">
                            <Trash2 className="w-4 h-4" data-id="element-8950" />
                          </button>
                        </div>
                      </div>

                      <div className="mt-auto space-y-2" data-id="element-8951">
                        <div className="flex items-center justify-between text-xs flex-wrap gap-2" data-id="element-8952">
                          <div className="flex items-center gap-2 flex-wrap" data-id="element-8953">
                            <span className="text-slate-600 font-medium" data-id="element-8954">
                              {completedCount} van {targetCount} klaar
                            </span>
                            {notStarted > 0 && (derived === 'open' || derived === 'verlopen') && <span className="inline-flex items-center gap-1 text-amber-700 font-medium text-[11px]" data-id="element-8955">
                                  · {notStarted} niet begonnen
                                </span>}
                          </div>
                          {derived === 'open' && <Badge variant="default" className="bg-indigo-100 text-indigo-700 text-[10px] py-0 px-1.5" data-id="element-8956">
                              Loopt nu
                            </Badge>}
                          {derived === 'klaar' && <Badge variant="success" className="bg-emerald-100 text-emerald-700 text-[10px] py-0 px-1.5" data-id="element-8957">
                              Afgerond
                            </Badge>}
                          {derived === 'verlopen' && <Badge variant="danger" className="bg-rose-100 text-rose-700 text-[10px] py-0 px-1.5" data-id="element-8958">
                              Deadline voorbij
                            </Badge>}
                          {derived === 'archief' && <Badge variant="default" className="bg-slate-200 text-slate-600 text-[10px] py-0 px-1.5" data-id="element-8959">
                              Gearchiveerd
                            </Badge>}
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-8960">
                          <div className={`h-full rounded-full transition-all duration-1000 ${derived === 'klaar' ? 'bg-emerald-500' : derived === 'verlopen' ? 'bg-rose-500' : derived === 'archief' ? 'bg-slate-400' : 'bg-indigo-500'}`} style={{
                    width: `${progressPercent}%`
                  }} data-id="element-8961" />
                        </div>
                      </div>
                    </Card>
                  </motion.div>;
        })}
          </motion.div> : <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.4
      }} data-id="element-8962">
            <EmptyState title={tab === 'open' ? 'Nog geen open huiswerk' : tab === 'klaar' ? 'Nog niets afgerond' : 'Archief is leeg'} message={tab === 'open' ? 'Zet huiswerk klaar voor je klas of specifieke leerlingen.' : tab === 'klaar' ? 'Zodra een opdracht door alle leerlingen is gemaakt verschijnt deze hier.' : 'Gearchiveerd huiswerk verschijnt hier.'} emotion={tab === 'klaar' ? 'encouraging' : 'thinking'} colorScheme="slate" data-id="element-8963" />
          </motion.div>}
      </motion.div>

      {activeAccount && <CreatePracticePanel open={isCreateOpen} onClose={handleCreateClose} students={students} selectedGroup={selectedGroup} teacherId={activeAccount.id} classroomId={selectedClassroomId || null} onSuccess={loadData} data-id="element-8964" />}

      <ConfirmDialog open={confirmAction.isOpen} title={confirmAction.title} message={confirmAction.message} confirmLabel="Bevestig" onConfirm={async () => {
      await confirmAction.action();
      setConfirmAction({
        ...confirmAction,
        isOpen: false
      });
    }} onCancel={() => setConfirmAction({
      ...confirmAction,
      isOpen: false
    })} data-id="element-8965" />
    </PageLayout>;
}